/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/* Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved. */

import React from 'react';
import { PlusIcon } from '@radix-ui/react-icons';
import { Button, PageTitle, Text } from '@/shared/ui';
import { PageInfoTooltip } from '@/shared/ui/components/basic/PageInfoTooltip';

interface TitleRowProps {
  title: string;
  icon?: React.ReactNode;
  /** Omit both `ctaLabel` and `onCtaClick` to render the header without a CTA button. */
  ctaLabel?: string;
  onCtaClick?: () => void;
  introText?: string;
  introTestIdPrefix?: string;
  /**
   * A visible one-liner next to the title, for an area whose purpose is not
   * obvious from its name. Distinct from `introText`, which hides the same
   * kind of sentence behind a tooltip.
   */
  subtitle?: string;
  /**
   * An action rendered immediately to the LEFT of the primary CTA, for a
   * secondary way into the same area -- the spreadsheet menu on the listings
   * is the first one. Headers that do not pass it are unchanged.
   */
  secondaryAction?: React.ReactNode;
}

export const PageHeaderTitleRow: React.FC<TitleRowProps> = ({
  title, icon, ctaLabel, onCtaClick, introText, introTestIdPrefix, subtitle, secondaryAction,
}) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--space-2)' }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
      <PageTitle icon={icon}>{title}</PageTitle>
      {introText && (
        <PageInfoTooltip text={introText} testIdPrefix={introTestIdPrefix ?? 'page'} />
      )}
      {subtitle && (
        <Text
          as="span"
          data-testid="page-header-subtitle"
          style={{ fontSize: 'var(--text-small)', color: 'var(--text-tertiary)', marginLeft: 'var(--space-2)' }}
        >
          {subtitle}
        </Text>
      )}
    </div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
    {secondaryAction}
    {ctaLabel && onCtaClick && (
      <Button
        data-tour="page-header-cta"
        type="button"
        variant="none"
        disableSize
        onClick={onCtaClick}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          padding: 'var(--space-2) var(--space-3)',
          fontSize: 'var(--text-body-sm)',
          fontWeight: 'var(--font-semibold)' as unknown as number,
          background: 'var(--button-primary-bg)',
          color: 'var(--button-primary-text)',
          border: 'none',
          borderRadius: 'var(--radius-md)',
          cursor: 'pointer',
          fontFamily: 'var(--font-family)',
          whiteSpace: 'nowrap',
        }}
      >
        <PlusIcon width={14} height={14} />
        {ctaLabel}
      </Button>
    )}
    </div>
  </div>
);
